// Settings. Edits to branding, theme, the add-in and data display are kept as
// one draft, previewed live, and saved together. Custom domain steps are real
// DNS and Azure operations, so they run immediately instead, as do auto-fill
// rules, which save one rule at a time.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Globe2, Lock, Paintbrush2, Palette, Plug, Settings as SettingsIcon, SlidersHorizontal, Wand2 } from 'lucide-react'
import { useBranding, BRANDING_DEFAULTS, THEME_TOKEN_DEFAULTS } from '../context/BrandingContext'
import { useDisplayPreferences } from '../context/DisplayPreferencesContext'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { useOpCo } from '../context/OpCoContext'
import { useTheme } from '../context/ThemeContext'
import { isReadOnlyUser, READ_ONLY_MESSAGE } from '../utils/readerAccess'
import api from '../services/api'
import {
  brandingDraftFrom, buildBrandingPayload, dirtySections, tokenDraftFrom, validationErrors,
} from '../components/settings/settingsModel'
import { useSwapIn } from '../components/settings/settingsMotion'
import { SectionHeader, SettingsNav } from '../components/settings/SettingsNav'
import { ConfirmDialog, SaveBar } from '../components/settings/SettingsPrimitives'
import { AppPreview, DataDisplayPreview, PluginPreview, PreviewFrame } from '../components/settings/SettingsPreview'
import BrandingSection from '../components/settings/BrandingSection'
import ThemeSection from '../components/settings/ThemeSection'
import PluginSection from '../components/settings/PluginSection'
import DisplaySection from '../components/settings/DisplaySection'
import DomainSection from '../components/settings/DomainSection'
import { useCustomDomain } from '../components/settings/useCustomDomain'
import AutofillRulesSettings from '../components/AutofillRulesSettings'
import BcConnectionsPanel from '../components/settings/BcConnectionsPanel'
import { useFeatures } from '../contexts/FeaturesContext'
import { OPCO_FLAG } from '../config/runtimeConfig'

const ORG_SETTINGS_MESSAGE = 'View only. These settings apply to every OpCo, so only an Admin can change them.'

// Nav order. `blurb` sits under the nav label; `description` opens the section.
const SECTIONS = [
  {
    id: 'branding', label: 'Branding', icon: Palette,
    blurb: 'Name, logo and colour',
    description: 'How your portal is named, and what people see in the sidebar and browser tab.',
  },
  {
    id: 'theme', label: 'Theme', icon: Paintbrush2,
    blurb: 'Buttons, statuses, toggles',
    description: 'Colours for buttons, statuses and toggles. Start from a theme or build your own.',
  },
  {
    id: 'display', label: 'Data display', icon: SlidersHorizontal,
    blurb: 'Field indicators',
    description: 'What reviewers see next to extracted fields. Applies to everyone in your organisation.',
  },
  {
    id: 'plugin', label: 'Outlook add-in', icon: Plug,
    blurb: 'Look and install',
    description: 'How the Outlook add-in looks, and how to install it for your users.',
  },
  {
    id: 'domain', label: 'Custom domain', icon: Globe2,
    blurb: 'Your own web address',
    description: 'Serve the portal from your own web address, such as portal.yourcompany.com.',
  },
  {
    id: 'autofill', label: 'Auto-fill rules', icon: Wand2,
    blurb: 'Fill fields by e-mail domain',
    description: 'Fill a field automatically based on the e-mail domain an order arrives at.',
  },
]
// Shown only to administrators of a deployment with Business Central and no OpCo
// workspaces. With OpCos on, each OpCo's connections are managed under Users & Groups.
const BC_SECTION = {
  id: 'bc', label: 'Business Central', icon: Plug,
  blurb: 'Connections',
  description: 'The Business Central environments and companies your templates post to.',
}
// Sections with nothing to preview take the full width.
const NO_PREVIEW = new Set(['autofill', 'bc'])
const SECTION_LABEL = Object.fromEntries([...SECTIONS, BC_SECTION].map(s => [s.id, s.label]))

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp', 'image/gif'])
const EMPTY_PLUGIN = { app_name: '', logo_url: null }
const LOCAL_HOST = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])$|\.local$|^\d{1,3}(\.\d{1,3}){3}$/

// Where each validation error lives, so "Show" can take the admin to it.
function errorTarget(key) {
  if (key.startsWith('token:')) return { section: 'theme', id: `settings-token-${key.slice(6)}` }
  return { section: 'branding', id: key === 'app_name' ? 'settings-app-name' : 'settings-brand-hex' }
}

// Logos are stored at branding/<org>/logo.<ext> and overwritten, so an add-in
// logo with the same extension replaces the portal's file. Returns that
// extension, or null when the URL isn't an uploaded logo.
function uploadedLogoExt(url) {
  if (!url) return null
  try {
    const match = new URL(url, window.location.origin).pathname.match(/\/branding\/[^/]+\/logo\.([a-z0-9]+)$/i)
    return match ? match[1].toLowerCase() : null
  } catch {
    return null
  }
}
const fileExt = file => (file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : 'png')

export default function Settings() {
  const {
    branding, loading: brandingLoading, updateBranding, resetBranding, uploadLogo, themeTokens, themeLibrary, saveThemeLibrary,
  } = useBranding()
  const { showConfidenceScore, showAiBadge, showColorCoding, updatePreferences } = useDisplayPreferences()
  const toast = useToast()
  const { user } = useAuth()
  const { isDark } = useTheme()
  const navigate = useNavigate()
  // These settings apply to every OpCo, so with OpCo workspaces on only an
  // OpCo admin can change them (the API answers 403 to anyone else, including
  // an org admin who manages one OpCo). Show that up front, not after a failed save.
  const { enabled: opcoOn, isOpcoAdmin } = useOpCo()
  const orgSettingsLocked = opcoOn && !isOpcoAdmin && user?.role !== 'admin'
  const isReader = isReadOnlyUser(user) || orgSettingsLocked
  const lockedMessage = orgSettingsLocked ? ORG_SETTINGS_MESSAGE : READ_ONLY_MESSAGE
  const locked = isReader || brandingLoading

  // Section, kept in the URL (?section=theme).
  const [params, setParams] = useSearchParams()
  const requested = params.get('section')
  const features = useFeatures()
  const sections = useMemo(
    () => (features.business_central && !OPCO_FLAG && user?.role === 'admin' ? [...SECTIONS, BC_SECTION] : SECTIONS),
    [features.business_central, user?.role],
  )
  const active = sections.some(s => s.id === requested) ? requested : 'branding'
  const go = useCallback(id => setParams({ section: id }, { replace: true }), [setParams])

  // Saved values and their drafts. Each draft re-seeds when the saved value changes.
  const saved = useMemo(() => brandingDraftFrom(branding, BRANDING_DEFAULTS), [branding])
  const [draft, setDraft] = useState(saved)
  useEffect(() => { setDraft(saved) }, [saved])

  const savedTokens = useMemo(() => tokenDraftFrom(themeTokens, THEME_TOKEN_DEFAULTS), [themeTokens])
  const [tokens, setTokens] = useState(savedTokens)
  useEffect(() => { setTokens(savedTokens) }, [savedTokens])

  const savedPrefs = useMemo(
    () => ({ showConfidenceScore, showAiBadge, showColorCoding }),
    [showConfidenceScore, showAiBadge, showColorCoding],
  )
  const [prefs, setPrefs] = useState(savedPrefs)
  useEffect(() => { setPrefs(savedPrefs) }, [savedPrefs])

  // Add-in name and logo only go into the manifest download, so they sit outside the draft.
  const [plugin, setPluginState] = useState(EMPTY_PLUGIN)
  const setPlugin = (key, value) => setPluginState(prev => ({ ...prev, [key]: value }))

  const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }))
  const setToken = (key, value) => setTokens(prev => ({ ...prev, [key]: value }))

  const dirty = useMemo(
    () => dirtySections({ draft, saved, tokens, savedTokens, prefs, savedPrefs }),
    [draft, saved, tokens, savedTokens, prefs, savedPrefs],
  )
  const errors = useMemo(() => validationErrors({ draft, tokens }), [draft, tokens])
  const errorCount = Object.keys(errors).length

  // Spotlight: what the admin is pointing at or editing, outlined in the preview.
  const [hoverSpot, setHoverSpot] = useState(null)
  const [focusSpot, setFocusSpot] = useState(null)
  const spot = hoverSpot ?? focusSpot
  const bindSpot = useCallback(target => ({
    onMouseEnter: () => setHoverSpot(target),
    onMouseLeave: () => setHoverSpot(cur => (cur === target ? null : cur)),
    onFocus: () => setFocusSpot(target),
    onBlur: () => setFocusSpot(cur => (cur === target ? null : cur)),
  }), [])
  const [themePreview, setThemePreview] = useState(null)
  useEffect(() => {
    setHoverSpot(null)
    setFocusSpot(null)
    setThemePreview(null)
  }, [active])

  // Save and discard.
  const [saving, setSaving] = useState(false)

  const save = useCallback(async () => {
    if (isReader) { toast.error(lockedMessage); return }
    if (!dirty.size || saving || errorCount) return

    const jobs = []
    const payload = buildBrandingPayload({ dirty, draft, saved, tokens, tokenDefaults: THEME_TOKEN_DEFAULTS })
    if (payload) {
      const label = [...dirty].filter(id => id !== 'display').map(id => SECTION_LABEL[id]).join(', ')
      jobs.push({ label, run: () => updateBranding(payload) })
    }
    if (dirty.has('display')) jobs.push({ label: SECTION_LABEL.display, run: () => updatePreferences(prefs) })

    setSaving(true)
    const results = await Promise.allSettled(jobs.map(job => job.run()))
    setSaving(false)

    const failures = results
      .map((result, i) => (result.status === 'rejected' ? { ...jobs[i], error: result.reason } : null))
      .filter(Boolean)
    if (!failures.length) {
      const names = SECTIONS.filter(s => dirty.has(s.id)).map(s => s.label)
      const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]
      toast.success(`${list} saved`, 'Changes are now live for everyone in your organisation.')
      return
    }
    const reason = failures[0].error?.response?.data?.error || 'Try again in a moment.'
    if (failures.length < jobs.length) {
      toast.error(`${failures.map(f => f.label).join(' and ')} not saved`, reason)
    } else {
      toast.error("Couldn't save", reason)
    }
  }, [isReader, dirty, saving, errorCount, draft, saved, tokens, prefs, updateBranding, updatePreferences, toast, lockedMessage])

  const discard = useCallback(() => {
    setDraft(saved)
    setTokens(savedTokens)
    setPrefs(savedPrefs)
  }, [saved, savedTokens, savedPrefs])

  const showFirstError = () => {
    const key = Object.keys(errors)[0]
    if (!key) return
    const target = errorTarget(key)
    go(target.section)
    setTimeout(() => {
      const el = document.getElementById(target.id)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el?.focus({ preventScroll: true })
    }, 120)
  }

  // Cmd/Ctrl+S saves. Always swallowed here; the browser's "Save page" is never wanted.
  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        save()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [save])

  // Unsaved edits: ask before closing the tab or following a link elsewhere in the app.
  const [leaveTo, setLeaveTo] = useState(null)
  useEffect(() => {
    if (!dirty.size) return undefined
    const onBeforeUnload = e => { e.preventDefault(); e.returnValue = '' }
    const onClick = e => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const link = e.target.closest?.('a[href]')
      if (!link || link.target === '_blank' || link.hasAttribute('download')) return
      const url = new URL(link.href, window.location.href)
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return
      e.preventDefault()
      e.stopPropagation()
      setLeaveTo(`${url.pathname}${url.search}${url.hash}`)
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    document.addEventListener('click', onClick, true)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      document.removeEventListener('click', onClick, true)
    }
  }, [dirty.size])

  const leave = () => {
    const to = leaveTo
    setLeaveTo(null)
    discard()
    navigate(to)
  }

  // Uploads.
  const [uploading, setUploading] = useState({ logo: false, favicon: false, plugin: false })
  const [clashFile, setClashFile] = useState(null)
  const [clashOpen, setClashOpen] = useState(false)

  const acceptImage = file => {
    if (isReader) { toast.error(lockedMessage); return false }
    if (!IMAGE_TYPES.has(file.type)) {
      toast.error('Unsupported image', 'Use a PNG, JPG, SVG, WebP or GIF.')
      return false
    }
    return true
  }

  // A just-uploaded logo is sampled from the local file, which never hits
  // cross-origin limits on reading pixels from the storage URL.
  const [localLogo, setLocalLogo] = useState(null) // { remote, local }
  useEffect(() => () => { if (localLogo) URL.revokeObjectURL(localLogo.local) }, [localLogo])
  const logoUrl = draft.logo_url || BRANDING_DEFAULTS.logo_url
  const logoSampleUrl = localLogo && localLogo.remote === draft.logo_url ? localLogo.local : undefined

  const uploadBrandImage = async (file, type) => {
    if (!acceptImage(file)) return
    setUploading(prev => ({ ...prev, [type]: true }))
    try {
      const url = await uploadLogo(file, type)
      if (type === 'logo') setLocalLogo({ remote: url, local: URL.createObjectURL(file) })
      setField(type === 'favicon' ? 'favicon_url' : 'logo_url', url)
    } catch (err) {
      toast.error('Upload failed', err?.response?.data?.error || 'Try again in a moment.')
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }))
    }
  }

  const uploadPluginLogo = async (file, confirmed = false) => {
    if (!acceptImage(file)) return
    if (!confirmed && [uploadedLogoExt(saved.logo_url), uploadedLogoExt(draft.logo_url)].includes(fileExt(file))) {
      setClashFile(file)
      setClashOpen(true)
      return
    }
    setClashOpen(false)
    setUploading(prev => ({ ...prev, plugin: true }))
    try {
      const url = await uploadLogo(file, 'logo')
      setPlugin('logo_url', url)
    } catch (err) {
      toast.error('Upload failed', err?.response?.data?.error || 'Try again in a moment.')
    } finally {
      setUploading(prev => ({ ...prev, plugin: false }))
    }
  }

  // Reset branding (immediate, after confirmation).
  const [confirmReset, setConfirmReset] = useState(false)
  const [resetting, setResetting] = useState(false)

  const doReset = async () => {
    if (isReader) { toast.error(lockedMessage); return }
    setResetting(true)
    try {
      await resetBranding()
      setPluginState(EMPTY_PLUGIN)
      setConfirmReset(false)
      toast.success('Branding reset', 'The default Xtract name, logo, colours and theme are back for everyone.')
    } catch (err) {
      toast.error("Couldn't reset branding", err?.response?.data?.error || 'Try again in a moment.')
    } finally {
      setResetting(false)
    }
  }

  // Manifest. The endpoint reads app_name and logo_url; without them it uses saved branding.
  const [downloading, setDownloading] = useState(false)

  const downloadManifest = async () => {
    if (isReader) { toast.error(lockedMessage); return }
    setDownloading(true)
    try {
      const qs = new URLSearchParams()
      if (!draft.apply_to_plugin) {
        if (plugin.app_name.trim()) qs.set('app_name', plugin.app_name.trim())
        if (plugin.logo_url) qs.set('logo_url', plugin.logo_url)
      }
      const query = qs.toString() ? `?${qs}` : ''
      const response = await api.get(`/branding/manifest${query}`, { responseType: 'blob' })
      const href = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }))
      const a = document.createElement('a')
      a.href = href
      a.download = 'manifest.xml'
      a.click()
      window.URL.revokeObjectURL(href)
    } catch (err) {
      console.error('Manifest download failed:', err)
      toast.error("Couldn't download the manifest", 'Try again in a moment.')
    } finally {
      setDownloading(false)
    }
  }

  // Custom domain.
  const domain = useCustomDomain({ isReader, toast })
  const [hostname, setHostname] = useState('')

  // The address shown in the preview: the domain being set up, the live custom
  // domain, or wherever the portal is served from (never a local dev address).
  const previewHost = useMemo(() => {
    const configured = domain.state?.configured ? domain.state.domain.hostname : null
    if (active === 'domain') {
      const typed = hostname.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '')
      if (configured || typed) return configured || typed
    }
    if (domain.status === 'active') return configured
    const host = window.location.hostname
    return LOCAL_HOST.test(host) ? 'xtract.synapx.delivery' : host
  }, [active, hostname, domain.state, domain.status])

  // Preview.
  const [previewDark, setPreviewDark] = useState(isDark)
  useEffect(() => { setPreviewDark(isDark) }, [isDark])

  const previewKind = active === 'plugin' ? 'plugin' : active === 'display' ? 'display' : 'app'
  const previewDraft = useMemo(() => ({
    ...draft,
    primary_color: themePreview?.primary_color ?? draft.primary_color,
    logo_url: draft.logo_url || BRANDING_DEFAULTS.logo_url,
    favicon_url: draft.favicon_url || BRANDING_DEFAULTS.favicon_url,
  }), [draft, themePreview])
  const previewTokens = themePreview?.tokens ?? tokens

  const preview = {
    app: (
      <PreviewFrame dark={previewDark} onDarkChange={setPreviewDark}>
        <AppPreview
          draft={previewDraft}
          tokens={previewTokens}
          tokenDefaults={THEME_TOKEN_DEFAULTS}
          host={previewHost}
          user={user}
          dark={previewDark}
          spot={spot}
        />
      </PreviewFrame>
    ),
    plugin: (
      <PreviewFrame dark={previewDark} onDarkChange={setPreviewDark}>
        <PluginPreview
          config={draft.apply_to_plugin
            ? previewDraft
            : { app_name: plugin.app_name.trim() || draft.app_name, primary_color: draft.primary_color, logo_url: plugin.logo_url || previewDraft.logo_url }}
          bodyText={draft.apply_to_plugin ? '' : draft.plugin_body_text.trim()}
          dark={previewDark}
          spot={spot}
        />
      </PreviewFrame>
    ),
    display: (
      <PreviewFrame>
        <DataDisplayPreview {...prefs} spot={spot} />
      </PreviewFrame>
    ),
  }[previewKind]

  const section = {
    branding: (
      <BrandingSection
        draft={draft}
        setField={setField}
        errors={errors}
        disabled={locked}
        uploading={uploading}
        onUpload={uploadBrandImage}
        onRequestReset={() => setConfirmReset(true)}
        resetting={resetting}
        bindSpot={bindSpot}
        logoUrl={logoUrl}
        logoSampleUrl={logoSampleUrl}
      />
    ),
    theme: (
      <ThemeSection
        tokens={tokens}
        savedTokens={savedTokens}
        savedPrimary={saved.primary_color}
        primaryColor={draft.primary_color}
        setToken={setToken}
        onPickTheme={theme => {
          setField('primary_color', theme.primary_color)
          setTokens(tokenDraftFrom(theme.tokens, THEME_TOKEN_DEFAULTS))
        }}
        onPreviewTheme={setThemePreview}
        onResetTokens={() => setTokens(tokenDraftFrom(THEME_TOKEN_DEFAULTS, THEME_TOKEN_DEFAULTS))}
        errors={errors}
        disabled={locked}
        bindSpot={bindSpot}
        themeLibrary={themeLibrary}
        saveThemeLibrary={saveThemeLibrary}
        toast={toast}
        logoUrl={logoUrl}
        logoSampleUrl={logoSampleUrl}
      />
    ),
    plugin: (
      <PluginSection
        applyToPlugin={draft.apply_to_plugin}
        setApplyToPlugin={v => setField('apply_to_plugin', v)}
        bodyText={draft.plugin_body_text}
        setBodyText={v => setField('plugin_body_text', v)}
        plugin={plugin}
        setPlugin={setPlugin}
        onPluginLogo={file => uploadPluginLogo(file)}
        uploadingLogo={uploading.plugin}
        portalName={draft.app_name}
        onDownload={downloadManifest}
        downloading={downloading}
        manifestStale={dirty.has('branding') || dirty.has('plugin')}
        disabled={locked}
        bindSpot={bindSpot}
      />
    ),
    display: (
      <DisplaySection
        prefs={prefs}
        setPref={(key, value) => setPrefs(prev => ({ ...prev, [key]: value }))}
        setAll={value => setPrefs({ showConfidenceScore: value, showAiBadge: value, showColorCoding: value })}
        disabled={isReader}
        bindSpot={bindSpot}
      />
    ),
    domain: (
      <DomainSection
        domain={domain}
        hostname={hostname}
        setHostname={setHostname}
        appName={saved.app_name}
        isReader={isReader}
        toast={toast}
        bindSpot={bindSpot}
      />
    ),
    autofill: <AutofillRulesSettings isReader={isReader} toast={toast} />,
    bc: <BcConnectionsPanel />,
  }[active]

  // Sections slide in from the direction of travel; the preview cross-fades when it changes kind.
  const index = sections.findIndex(s => s.id === active)
  const lastIndex = useRef(index)
  const direction = index >= lastIndex.current ? 1 : -1
  useEffect(() => { lastIndex.current = index }, [index])
  const contentRef = useRef(null)
  const previewRef = useRef(null)
  useSwapIn(contentRef, active, { y: 14 * direction })
  useSwapIn(previewRef, previewKind, { y: 0, scale: 0.985, duration: 380 })

  const barState = saving ? 'saving' : dirty.size ? (errorCount ? 'error' : 'dirty') : 'idle'

  return (
    <div className="animate-page-in">
      <header className="mb-6 flex items-center gap-3.5 animate-header-in">
        <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
          <SettingsIcon className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage how {saved.app_name} looks and works for your organisation.
          </p>
        </div>
      </header>

      {isReader && (
        <p className="mb-5 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-[13px] text-gray-600
                      dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300">
          <Lock className="h-4 w-4 shrink-0 text-gray-400" /> {orgSettingsLocked && !isReadOnlyUser(user) ? ORG_SETTINGS_MESSAGE : 'View only. Ask an admin to make changes.'}
        </p>
      )}

      <SettingsNav
        sections={sections}
        active={active}
        onChange={go}
        dirty={dirty}
        domainStatus={domain.status}
      />

      <div>
        <div className={NO_PREVIEW.has(active)
          ? 'grid grid-cols-1 items-start gap-6'
          : 'grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_19rem] 2xl:grid-cols-[minmax(0,1fr)_22rem]'}
        >
          <div ref={contentRef} className="min-w-0">
            <SectionHeader section={sections[index]} dirty={dirty.has(active)} />
            {section}
          </div>
          {!NO_PREVIEW.has(active) && (
            <aside ref={previewRef} className="min-w-0 max-w-[34rem] xl:sticky xl:top-[5.5rem] xl:max-w-none">
              {preview}
            </aside>
          )}
        </div>
      </div>

      {!isReader && (
        <SaveBar state={barState} errorCount={errorCount} onSave={save} onDiscard={discard} onShowError={showFirstError} />
      )}

      <ConfirmDialog
        open={confirmReset}
        title="Reset branding?"
        body="This restores the default Xtract name, logo, colours and theme for everyone."
        confirmLabel="Reset"
        busy={resetting}
        onConfirm={doReset}
        onCancel={() => setConfirmReset(false)}
      />

      <ConfirmDialog
        open={clashOpen}
        title="Replace the portal logo too?"
        body={clashFile && (
          <>
            Uploading a {fileExt(clashFile).toUpperCase()} here also replaces your {fileExt(clashFile).toUpperCase()} portal logo.
            Use a different file type to keep them separate.
          </>
        )}
        confirmLabel="Upload anyway"
        onConfirm={() => uploadPluginLogo(clashFile, true)}
        onCancel={() => setClashOpen(false)}
      />

      <ConfirmDialog
        open={Boolean(leaveTo)}
        title="Leave without saving?"
        body="Your unsaved changes will be lost."
        cancelLabel="Keep editing"
        confirmLabel="Leave"
        onConfirm={leave}
        onCancel={() => setLeaveTo(null)}
      />
    </div>
  )
}
